import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { FileText, Download, Plus, Eye, Loader2, Trash2, AlertTriangle } from "lucide-react";

const statusBadge = (a) => {
  if (a.paid) return { label: "Full report ready", cls: "bg-forest/10 text-forest border-forest/30" };
  if (a.status === "completed") return { label: "Snapshot ready", cls: "bg-ochre/15 text-gold-dark border-ochre/40" };
  return { label: "Partway through", cls: "bg-clay/10 text-clay border-clay/30" };
};

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    api.get("/me/assessments").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/assessments/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "Fundability_Blueprint.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Report not available yet."); }
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/me");
      setUser(false);
      toast.success("Your account and data have been deleted.");
      navigate("/");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-navy">Your dashboard</h1>
          <p className="mt-1 text-muted-foreground">Welcome back{user?.name ? `, ${user.name}` : ""}.</p>
        </div>
        <Link to="/assessment/new" data-testid="new-assessment-btn" className="btn-gold font-semibold px-6 py-3 rounded-[3px] flex items-center gap-2 self-start">
          <Plus size={18} /> Start a new check
        </Link>
      </div>

      {items === null ? (
        <div className="py-24 text-center"><Loader2 className="animate-spin mx-auto text-gold" /></div>
      ) : items.length === 0 ? (
        <div className="mt-10 p-12 bg-white border border-line rounded-md text-center">
          <FileText className="mx-auto text-line mb-4" size={40} />
          <p className="text-ink/70 max-w-md mx-auto">You haven't finished a check yet. Your first snapshot will show what's working and what to strengthen — it only takes a few minutes.</p>
          <Link to="/assessment/new" className="mt-6 inline-block btn-gold font-semibold px-6 py-3 rounded-[3px]">Check Your Funding Readiness</Link>
        </div>
      ) : (
        <div className="mt-10 space-y-4" data-testid="assessment-list">
          {items.map((a) => {
            const b = statusBadge(a);
            return (
              <div key={a.id} className="p-6 bg-white border border-line rounded-md hover-lift flex flex-col md:flex-row md:items-center gap-5" data-testid={`assessment-row-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display font-semibold text-navy text-lg">{a.org_name}</h3>
                    <span className={`text-xs px-2.5 py-1 rounded-full border ${b.cls}`}>{b.label}</span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {a.completed_at ? `Completed ${new Date(a.completed_at).toLocaleDateString()}` : `Started ${new Date(a.created_at).toLocaleDateString()}`}
                    {a.overall_score != null && ` · Score ${a.overall_score}/100 · ${a.readiness_category || ""}`}
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {a.status !== "completed" && (
                    <Link to={`/assessment/${a.id}`} className="px-4 py-2 border border-navy text-navy rounded-[3px] text-sm font-medium hover:bg-navy hover:text-ivory transition">Continue</Link>
                  )}
                  {a.status === "completed" && !a.paid && (
                    <Link to={`/results/${a.id}`} className="px-4 py-2 border border-navy text-navy rounded-[3px] text-sm font-medium hover:bg-navy hover:text-ivory transition flex items-center gap-1.5"><Eye size={15} /> View snapshot</Link>
                  )}
                  {a.paid && (
                    <>
                      <Link to={`/report/${a.id}`} data-testid={`view-report-${a.id}`} className="px-4 py-2 btn-gold rounded-[3px] text-sm font-semibold flex items-center gap-1.5"><Eye size={15} /> View report</Link>
                      <button onClick={() => downloadPdf(a.id)} data-testid={`download-${a.id}`} className="px-4 py-2 border border-line text-navy rounded-[3px] text-sm font-medium flex items-center gap-1.5 hover:border-gold transition"><Download size={15} /> PDF</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Danger zone */}
      <div className="mt-16 p-6 border border-red-200 bg-red-50/50 rounded-md">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-red-600 mt-0.5" size={20} />
          <div className="flex-1">
            <h3 className="font-display font-semibold text-navy">Delete account & data</h3>
            <p className="mt-1 text-sm text-charcoal/70">Permanently delete your account, all assessments, reports, and uploaded documents. This cannot be undone.</p>
            {confirmDelete ? (
              <div className="mt-4 flex gap-3">
                <button onClick={deleteAccount} data-testid="confirm-delete" className="px-4 py-2 bg-red-600 text-white rounded-[3px] text-sm font-semibold flex items-center gap-1.5"><Trash2 size={15} /> Yes, delete everything</button>
                <button onClick={() => setConfirmDelete(false)} className="px-4 py-2 border border-line text-charcoal rounded-[3px] text-sm">Cancel</button>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)} data-testid="delete-account" className="mt-4 px-4 py-2 border border-red-300 text-red-700 rounded-[3px] text-sm font-medium hover:bg-red-100 transition">Delete my account</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
